<script setup>
import { nextTick, onBeforeUnmount, ref } from 'vue';
import { useEventListener } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  disabled: { type: Boolean, default: false },
  onCapture: { type: Function, required: true },
});

const { t } = useI18n();
const isCaptureMode = ref(false);
const isRendering = ref(false);
const isDragging = ref(false);
const viewportWidth = ref(0);
const viewportHeight = ref(0);
const selectionStart = ref({ x: 0, y: 0 });
const selection = ref({ x: 0, y: 0, width: 0, height: 0 });

const maskId = `screen-capture-mask-${Math.random().toString(36).slice(2)}`;

const canvasToBlob = canvas =>
  new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('screen_capture_failed'));
      }
    }, 'image/png');
  });

const resetSelection = () => {
  selection.value = { x: 0, y: 0, width: 0, height: 0 };
  selectionStart.value = { x: 0, y: 0 };
  isDragging.value = false;
};

const cancelCapture = () => {
  isCaptureMode.value = false;
  resetSelection();
};

const startCaptureMode = () => {
  if (props.disabled || isRendering.value) return;

  viewportWidth.value = window.innerWidth;
  viewportHeight.value = window.innerHeight;
  resetSelection();
  isCaptureMode.value = true;
};

const pointFromEvent = event => ({
  x: Math.min(viewportWidth.value, Math.max(0, event.clientX)),
  y: Math.min(viewportHeight.value, Math.max(0, event.clientY)),
});

const updateSelection = point => {
  const x = Math.min(selectionStart.value.x, point.x);
  const y = Math.min(selectionStart.value.y, point.y);
  selection.value = {
    x,
    y,
    width: Math.abs(point.x - selectionStart.value.x),
    height: Math.abs(point.y - selectionStart.value.y),
  };
};

const startSelection = event => {
  if (event.button !== 0 || isRendering.value) return;

  selectionStart.value = pointFromEvent(event);
  selection.value = {
    x: selectionStart.value.x,
    y: selectionStart.value.y,
    width: 0,
    height: 0,
  };
  isDragging.value = true;
  event.currentTarget.setPointerCapture(event.pointerId);
};

const moveSelection = event => {
  if (!isDragging.value) return;
  updateSelection(pointFromEvent(event));
};

const screenshotFilename = () => {
  const stamp = new Date().toISOString().replace(/[-:]/g, '').slice(0, 15);
  return `screenshot-${stamp}.png`;
};

const renderSelection = async () => {
  const crop = {
    x: Math.round(selection.value.x),
    y: Math.round(selection.value.y),
    width: Math.max(1, Math.round(selection.value.width)),
    height: Math.max(1, Math.round(selection.value.height)),
  };
  const scale = window.devicePixelRatio || 1;

  isCaptureMode.value = false;
  isRendering.value = true;
  await nextTick();

  try {
    const { default: html2canvas } = await import('html2canvas');
    const pageCanvas = await html2canvas(document.documentElement, {
      x: window.scrollX,
      y: window.scrollY,
      width: viewportWidth.value,
      height: viewportHeight.value,
      windowWidth: viewportWidth.value,
      windowHeight: viewportHeight.value,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      scale,
      useCORS: true,
      logging: false,
      ignoreElements: element =>
        element.hasAttribute('data-html2canvas-ignore'),
    });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(crop.width * scale);
    canvas.height = Math.round(crop.height * scale);
    canvas
      .getContext('2d')
      .drawImage(
        pageCanvas,
        Math.round(crop.x * scale),
        Math.round(crop.y * scale),
        canvas.width,
        canvas.height,
        0,
        0,
        canvas.width,
        canvas.height
      );

    const blob = await canvasToBlob(canvas);
    const name = screenshotFilename();
    const file = new File([blob], name, { type: 'image/png' });
    props.onCapture({ name, type: file.type, size: file.size, file });
  } catch {
    useAlert(t('CONVERSATION.REPLYBOX.SCREEN_CAPTURE.ERROR'));
  } finally {
    isRendering.value = false;
    resetSelection();
  }
};

const finishSelection = async event => {
  if (!isDragging.value) return;

  updateSelection(pointFromEvent(event));
  isDragging.value = false;
  if (event.currentTarget.hasPointerCapture(event.pointerId)) {
    event.currentTarget.releasePointerCapture(event.pointerId);
  }

  if (selection.value.width < 8 || selection.value.height < 8) {
    resetSelection();
    return;
  }

  await renderSelection();
};

const onKeydown = event => {
  if (isCaptureMode.value && event.key === 'Escape') {
    cancelCapture();
    event.preventDefault();
  }
};

const onResize = () => {
  if (isCaptureMode.value) cancelCapture();
};

useEventListener(window, 'keydown', onKeydown);
useEventListener(window, 'resize', onResize);
onBeforeUnmount(cancelCapture);
</script>

<template>
  <Button
    v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_SCREEN_CAPTURE_ICON')"
    data-html2canvas-ignore
    icon="i-ph-scan"
    slate
    faded
    sm
    :disabled="disabled || isRendering"
    :is-loading="isRendering"
    @click="startCaptureMode"
  />

  <Teleport to="body">
    <div
      v-if="isCaptureMode"
      data-html2canvas-ignore
      class="fixed inset-0 z-[10000] cursor-crosshair select-none touch-none"
      @pointerdown="startSelection"
      @pointermove="moveSelection"
      @pointerup="finishSelection"
      @pointercancel="cancelCapture"
      @contextmenu.prevent="cancelCapture"
    >
      <svg
        :width="viewportWidth"
        :height="viewportHeight"
        :viewBox="`0 0 ${viewportWidth} ${viewportHeight}`"
        class="absolute inset-0 size-full"
        aria-hidden="true"
        preserveAspectRatio="none"
      >
        <defs>
          <mask :id="maskId">
            <rect
              x="0"
              y="0"
              :width="viewportWidth"
              :height="viewportHeight"
              fill="white"
            />
            <rect
              :x="selection.x"
              :y="selection.y"
              :width="selection.width"
              :height="selection.height"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          :width="viewportWidth"
          :height="viewportHeight"
          fill="black"
          fill-opacity="0.45"
          :mask="`url(#${maskId})`"
        />
        <rect
          :x="selection.x"
          :y="selection.y"
          :width="selection.width"
          :height="selection.height"
          class="fill-transparent stroke-n-brand"
          stroke-width="2"
          vector-effect="non-scaling-stroke"
        />
      </svg>

      <div
        class="pointer-events-none absolute left-1/2 top-5 -translate-x-1/2 rounded-lg bg-n-alpha-black2 px-4 py-2 text-sm font-medium text-white shadow-lg"
      >
        {{ $t('CONVERSATION.REPLYBOX.SCREEN_CAPTURE.INSTRUCTION') }}
      </div>
    </div>
  </Teleport>
</template>
